/**
 * <ModelPickerField> — reusable grouped-by-provider model picker (AC-1547,
 * epic AC-1537 step-level model selection).
 *
 * Extracted verbatim from the AC-1516 picker logic that previously lived
 * only inside `AgentFormModal.tsx`. The agent edit modal AND
 * `StepConfigurationModal` (per the step-level model selection epic) share
 * this component so the BYOK gating, pricing labels, missing-key CTA, and
 * tier badges stay consistent across the two surfaces.
 *
 * The component is self-contained: it pulls `llmCatalog` + `llmByokMode`
 * from `useProjectStore` and the user's configured API keys from
 * `useApiKeys` internally so callers don't have to thread props for
 * shared state. Override props remain available for tests that want to
 * inject a deterministic catalog / configured-provider set.
 *
 * Notable behaviors:
 *  - Group headers (one per provider) render as `aria-disabled="true"` rows
 *    so the underlying CustomSelect treats them as non-interactive dividers.
 *  - Pricing labels render inline on each row as `"$IN / $OUT per 1M tokens"`.
 *  - In BYOK mode, models whose provider the user has not configured render
 *    disabled with an "(add key)" suffix and the listbox de-prioritizes
 *    arrow-key navigation onto them. If the currently-selected model has no
 *    key, a CTA below the picker links to the API Keys settings page.
 *  - When the catalog has not loaded yet, a minimal fallback list ensures
 *    the picker still renders and any "Switch to GPT-4o" affordance works
 *    (matches the pre-AC-1347 behavior that the byokPicker tests exercised).
 */

import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import CustomSelect from './CustomSelect';
import { useProjectStore } from '../../store';
import { useApiKeys } from '../../hooks/queries/useApiKeys';
import { LLMCatalogEntry } from '../../types';

export interface ModelPickerFieldProps {
  /** Current selected model id (e.g. "gpt-4o-mini"). */
  value: string;
  /** Called with the new model id when the user picks a different row. */
  onChange: (modelId: string) => void;
  /** When true, render the trigger as disabled. */
  disabled?: boolean;
  /** Override the field label. Defaults to "Language Model". */
  label?: string;
  /**
   * Help text rendered immediately below the picker. Defaults to the
   * description from `AgentFormModal` ("The underlying language model that
   * processes instructions and generates responses").
   */
  helpText?: string;
  /**
   * Optional pre-loaded catalog. Falls back to `useProjectStore().llmCatalog`
   * when undefined. Tests pass this to avoid threading the store mock.
   */
  llmCatalog?: LLMCatalogEntry[];
  /** Optional pre-loaded BYOK mode flag. Falls back to the store. */
  llmByokMode?: boolean;
  /**
   * Optional pre-loaded "providers with configured keys" set. Falls back to
   * the `useApiKeys()` query.
   */
  configuredProviders?: Set<string>;
  /** Optional inline error message (matches AgentFormModal's red-border style). */
  error?: string;
  /** Test id for the underlying CustomSelect. */
  testId?: string;
}

const PROVIDER_LABEL: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  google: 'Google',
};

const DEFAULT_HELP_TEXT =
  'The underlying language model that processes instructions and generates responses';

const ModelPickerField: React.FC<ModelPickerFieldProps> = ({
  value,
  onChange,
  disabled = false,
  label = 'Language Model',
  helpText = DEFAULT_HELP_TEXT,
  llmCatalog: catalogProp,
  llmByokMode: byokProp,
  configuredProviders: configuredProp,
  error,
  testId,
}) => {
  const store = useProjectStore();
  const llmCatalog = catalogProp ?? store.llmCatalog ?? [];
  const llmByokMode = byokProp ?? store.llmByokMode ?? false;

  // Internal apiKeys query — only consumed when no override is provided.
  // We pass `enabled` so the query is skipped when configuredProp is supplied.
  const apiKeysQuery = useApiKeys({ enabled: configuredProp === undefined });
  const configuredProviders = useMemo(() => {
    if (configuredProp !== undefined) return configuredProp;
    const s = new Set<string>();
    for (const k of apiKeysQuery.data ?? []) {
      if (k.has_key) s.add(k.provider);
    }
    return s;
  }, [configuredProp, apiKeysQuery.data]);

  const options = useMemo(() => {
    const catalog = llmCatalog || [];
    if (catalog.length === 0) {
      // Pre-AC-1347 fallback used when the catalog hasn't loaded yet. Must
      // include `gpt-4o` so any "Switch to GPT-4o" affordance still resolves
      // to a valid CustomSelect row.
      return [
        { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
        { value: 'gpt-4o', label: 'GPT-4o' },
        { value: 'o3-mini', label: 'o3 Mini' },
      ];
    }

    // Group by provider, falling back to "Other" for entries that didn't
    // ship a provider field.
    const groups = new Map<string, typeof catalog>();
    for (const entry of catalog) {
      const key = (entry as any).provider || 'other';
      if (!groups.has(key)) groups.set(key, []);
      (groups.get(key) as typeof catalog).push(entry);
    }

    const fmtPrice = (n?: number) =>
      typeof n === 'number' ? `$${n.toFixed(n < 1 ? 2 : 0)}` : null;

    const opts: Array<{
      value: string;
      label: string;
      description?: string;
      disabled?: boolean;
    }> = [];

    for (const [providerKey, entries] of groups) {
      const providerLabel = PROVIDER_LABEL[providerKey] || providerKey;
      opts.push({
        value: `__group_${providerKey}`,
        label: providerLabel,
        disabled: true,
      });

      const providerKeyMissing =
        llmByokMode &&
        providerKey !== 'other' &&
        !configuredProviders.has(providerKey);

      for (const entry of entries) {
        const inPrice = fmtPrice((entry as any).input_price_per_1m);
        const outPrice = fmtPrice((entry as any).output_price_per_1m);
        const priceLine =
          inPrice && outPrice ? `${inPrice} / ${outPrice} per 1M tokens` : null;
        const tierLine = (entry as any).tier ? `Tier: ${(entry as any).tier}` : null;
        const missingKeyLine = providerKeyMissing ? 'Add a key to use' : null;
        const subtitleBits = [tierLine, priceLine, missingKeyLine].filter(Boolean);
        const subtitle = subtitleBits.length
          ? subtitleBits.join(' - ')
          : undefined;
        const labelSuffix = providerKeyMissing ? ' (add key)' : '';
        const optionLabel = priceLine
          ? `${entry.label} - ${priceLine}${labelSuffix}`
          : `${entry.label}${labelSuffix}`;

        const explicitlyAvailable = (entry as any).available !== false;
        const isUsable =
          !llmByokMode ||
          providerKey === 'other' ||
          configuredProviders.has(providerKey) ||
          (entry as any).available === true;

        opts.push({
          value: entry.id,
          label: optionLabel,
          description: subtitle,
          disabled:
            !isUsable && !explicitlyAvailable
              ? true
              : !isUsable
                ? true
                : undefined,
        });
      }
    }

    return opts;
  }, [llmCatalog, llmByokMode, configuredProviders]);

  const selectedEntry = useMemo(
    () => (llmCatalog || []).find((e) => e.id === value),
    [llmCatalog, value],
  );

  const selectedProviderMissingKey =
    llmByokMode &&
    selectedEntry &&
    (selectedEntry as any).provider &&
    !configuredProviders.has((selectedEntry as any).provider);

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {label}
      </label>
      <CustomSelect
        value={value || 'gpt-4o-mini'}
        onChange={(v) => onChange(v)}
        options={options}
        className={`w-full ${error ? 'border-red-300' : ''}`}
        disabled={disabled}
        aria-label={testId || label}
      />
      {helpText && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {helpText}
        </p>
      )}
      {selectedProviderMissingKey && (
        <div
          role="alert"
          data-testid="model-picker-missing-key-cta"
          className="mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg"
        >
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            This model requires a {(selectedEntry as any)?.provider} API
            key.{' '}
            <Link
              to="/settings/api-keys"
              className="font-medium underline hover:no-underline"
            >
              Add a key
            </Link>{' '}
            to use it.
          </p>
        </div>
      )}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default ModelPickerField;
